import { Events } from './events';
import { withProvenance } from './provenance';

type Annotation = {
    id: string;
    worldPosition: [number, number, number];
    worldNormal?: [number, number, number];
    type: 'pin' | 'evidence' | 'witness' | 'vehicle' | 'poi' | 'damage';
    label: string;
    description?: string;
    exhibitId?: string;
    layerId?: string;
    linkedEvidenceIds?: string[];
    author: string;
    createdAt: string;
};

type AnnotationLayer = {
    id: string;
    name: string;
    visible: boolean;
    locked: boolean;
    color?: [number, number, number];
};

const cloneAnnotation = (annotation: Annotation): Annotation => {
    return {
        ...annotation,
        worldPosition: [annotation.worldPosition[0], annotation.worldPosition[1], annotation.worldPosition[2]],
        worldNormal: annotation.worldNormal ? [annotation.worldNormal[0], annotation.worldNormal[1], annotation.worldNormal[2]] : undefined,
        linkedEvidenceIds: annotation.linkedEvidenceIds?.slice()
    };
};

const cloneLayer = (layer: AnnotationLayer): AnnotationLayer => {
    return {
        ...layer,
        color: layer.color ? [layer.color[0], layer.color[1], layer.color[2]] : undefined
    };
};

const registerAnnotationsEvents = (events: Events) => {
    let annotations: Annotation[] = [];
    let layers: AnnotationLayer[] = [];
    let selectedId: string | null = null;

    const listAnnotations = () => annotations.map(cloneAnnotation);
    const listLayers = () => layers.map(cloneLayer);

    const fireListChanged = () => {
        events.fire('annotations.listChanged', listAnnotations());
    };

    const fireLayersChanged = () => {
        events.fire('annotations.layersChanged', listLayers());
    };

    events.function('annotations.list', () => listAnnotations());
    events.function('annotations.layers', () => listLayers());
    events.function('annotations.selected', () => selectedId);

    events.on('annotations.select', (id: string | null) => {
        const next = typeof id === 'string' ? id : null;
        if (next === selectedId) {
            return;
        }
        selectedId = next;
        events.fire('annotations.selectedChanged', selectedId);
    });

    events.on('annotations.add', (annotation: Annotation) => {
        if (!annotation || typeof annotation.id !== 'string') {
            return;
        }
        if (annotations.some(existing => existing.id === annotation.id)) {
            return;
        }
        const stamped = withProvenance(events, annotation);
        annotations = annotations.concat(cloneAnnotation(stamped));
        fireListChanged();
    });

    events.on('annotations.remove', (id: string) => {
        if (typeof id !== 'string') {
            return;
        }
        const next = annotations.filter(annotation => annotation.id !== id);
        if (next.length === annotations.length) {
            return;
        }
        annotations = next;
        if (selectedId === id) {
            selectedId = null;
        }
        fireListChanged();
    });

    events.on('annotations.update', (payloadOrId: { id: string; patch: Partial<Annotation> } | string, patchArg?: Partial<Annotation>) => {
        const id = typeof payloadOrId === 'string' ? payloadOrId : payloadOrId?.id;
        const patch = typeof payloadOrId === 'string' ? patchArg : payloadOrId?.patch;

        if (typeof id !== 'string' || !patch) {
            return;
        }

        let changed = false;
        annotations = annotations.map((annotation) => {
            if (annotation.id !== id) {
                return annotation;
            }
            changed = true;
            return cloneAnnotation({
                ...annotation,
                ...patch
            } as Annotation);
        });

        if (changed) {
            fireListChanged();
        }
    });

    events.on('annotations.setAll', (next: Annotation[] | undefined) => {
        annotations = Array.isArray(next) ? next.filter(item => !!item && typeof item.id === 'string').map(cloneAnnotation) : [];
        if (selectedId && !annotations.some(annotation => annotation.id === selectedId)) {
            selectedId = null;
        }
        fireListChanged();
    });

    events.on('annotations.layers.add', (layer: AnnotationLayer) => {
        if (!layer || typeof layer.id !== 'string') {
            return;
        }
        if (layers.some(existing => existing.id === layer.id)) {
            return;
        }
        layers = layers.concat(cloneLayer(layer));
        fireLayersChanged();
    });

    events.on('annotations.layers.setVisible', (id: string, visible: boolean) => {
        if (typeof id !== 'string') {
            return;
        }
        let changed = false;
        layers = layers.map((layer) => {
            if (layer.id !== id) {
                return layer;
            }
            const nextVisible = !!visible;
            if (nextVisible === layer.visible) {
                return layer;
            }
            changed = true;
            return {
                ...layer,
                visible: nextVisible
            };
        });

        if (changed) {
            fireLayersChanged();
        }
    });

    events.on('annotations.layers.rename', (id: string, name: string) => {
        if (typeof id !== 'string' || typeof name !== 'string') {
            return;
        }
        const trimmed = name.trim();
        if (!trimmed) {
            return;
        }
        let changed = false;
        layers = layers.map((layer) => {
            if (layer.id !== id) {
                return layer;
            }
            if (layer.name === trimmed) {
                return layer;
            }
            changed = true;
            return { ...layer, name: trimmed };
        });
        if (changed) {
            fireLayersChanged();
        }
    });

    events.on('annotations.layers.setAll', (next: AnnotationLayer[] | undefined) => {
        layers = Array.isArray(next) ? next.filter(item => !!item && typeof item.id === 'string').map(cloneLayer) : [];
        fireLayersChanged();
    });

    events.on('scene.clear', () => {
        annotations = [];
        layers = [];
        selectedId = null;
        fireListChanged();
        fireLayersChanged();
    });

    fireListChanged();
    fireLayersChanged();
};

export { registerAnnotationsEvents };
export type { Annotation, AnnotationLayer };
